import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SimpleBar from "simplebar-react";
import { Collapse, Container } from "reactstrap";
import sidebarRoutes from "../components/Json/sidebarRoutes.json";
import logo from "../Assets/logo.svg";
import LogoutModal from "./common/LogoutConfrimModal.tsx";
import { fetchDataSource } from "../modules/datasetModule.ts";
import { keycloak } from "./login/keycloakConfig.tsx";
import axios from "axios";

const Sidebar = () => {
  const location = useLocation();
  const [openSections, setOpenSections] = useState({});
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [datasource, setDatasource] = useState(["csv"]);
  const redirectedFromUMP = sessionStorage.getItem("redirectedFromUMP") === "true";

  const isPathActive = (route) => {
    if (!route) return false;
    if (location.pathname === route.path) return true;
    if (route.additionalPaths && route.additionalPaths.length > 0) {
      return route.additionalPaths.some(path => location.pathname === path);
    }
    return false;
  };

  const handleNavigateToUMP = () => {
    const homeTab = window.open(`${window.env.REACT_APP_UMP_REDIRECT_URL}/dashboard`, "homeTab");
    if (homeTab) {
      homeTab.focus();
    }
  };

  const isRouteActive = (route, pathname) => {
    if (!route) return false;

    if (pathname === route.path) return true;

    if (route.additionalPaths?.length) {
      return route.additionalPaths.includes(pathname);
    }

    return false;
  };
    // Check if any route in a section is active
  const isSectionActive = (section, pathname) => {
    return section.routes.some(route => isRouteActive(route, pathname));
  };

  useEffect(() => {
    setOpenSections(prev => {
      const updated = { ...prev };

      sidebarRoutes.forEach(section => {
        if (isSectionActive(section, location.pathname)) {
          updated[section.section] = true;
        }
      });

      return updated;
    });
  }, [location.pathname]);

  useEffect(() => {
    const getDatasource = async () => {
      try {
        const response = await fetchDataSource();
        setDatasource(response.data_source_list);
        sessionStorage.setItem("datasource", JSON.stringify(datasource));
      } catch (error) {
        console.error("Error fetching datasource configuration:", error);
        setDatasource(["csv"])
      }
    };
    getDatasource();
  }, []);

  // Toggle section collapse
  const toggleSection = (sectionName) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const handleLogout = async () => {
    try {
      if (sessionStorage.getItem("redirectedFromUMP") === "true") {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = `${window.env.REACT_APP_UMP_REDIRECT_URL}/home`;
        return;
      }

      const environment = localStorage.getItem("environment");
      if (environment === "Native") {
        try {
          await axios.put(
            `${window.env.REACT_APP_UMP_URL}/logout`,
            {},
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("access_token")}`
              }
            }
          );
        } catch (err) {
          console.error('Backend logout failed:', err);
        }
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = "/signIn";
        return;
      }

      if (environment === "Keycloak" || environment === "Azure" || environment === "Google") {
        if (keycloak && keycloak.authenticated) {
          sessionStorage.clear();
          localStorage.clear();
          keycloak.logout({
            redirectUri: `${window.env.REACT_APP_UI_URL}/signIn`,
          });
        } else {
          sessionStorage.clear();
          localStorage.clear();
          window.location.href = "/signIn";
        }
        return;
      }
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/signIn";
    } catch (err) {
      console.error("Error logging out:", err);
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/signIn";
    }
  };

  return (
    <React.Fragment>
      <div className="app-menu navbar-menu">
        <div className="navbar-brand-box py-2">
          <div className="p-4 flex justify-center">
            <span className="logo-lg menu-icon">
              <img src={logo} alt="Logo" height="40" />
            </span>
          </div>
        </div>
        <React.Fragment>
          <SimpleBar id="scrollbar" className="h-100">
            <Container fluid>
              <div id="two-column-menu"></div>
              <ul className="navbar-nav" id="navbar-nav" style={{ paddingBottom: '180px' }}>
                {sidebarRoutes.map((section, sectionIndex) => {
                  const isActive = isSectionActive(section);
                  const isOpen = openSections[section.section];
                  const hasMultipleRoutes = section.routes.length > 1;

                  return (
                    <React.Fragment key={sectionIndex}>
                      {hasMultipleRoutes ? (
                        <li className="nav-item sidebar-menu-item">
                          <Link
                            onClick={(e) => {
                              e.preventDefault();
                              toggleSection(section.section);
                            }}
                            className={`nav-link menu-link ${isActive ? 'active' : ''}`}
                            to="/#"
                            data-bs-toggle="collapse"
                          >
                            <div className="d-flex align-items-start">
                              <i className={section.icon}></i>
                              <span data-key="t-apps">
                                {section.section.split(' ').map((word, idx) => (
                                  <React.Fragment key={idx}>
                                    {word}
                                    {idx < section.section.split(' ').length - 1 && <br />}
                                  </React.Fragment>
                                ))}
                              </span>
                            </div>
                          </Link>

                          <Collapse
                            className="menu-dropdown"
                            isOpen={isOpen}
                            id={`sidebar-${section.section}`}
                            style={{ visibility: 'visible' }}
                          >
                            <ul className="nav nav-sm flex-column">
                              {section.routes.map((route, routeIndex) => (
                                <li className="nav-item" key={routeIndex}>
                                  <Link
                                    to={route.path}
                                    className={`nav-link ms-4 ${isPathActive(route) ? 'active' : ''
                                      }`}
                                  >
                                    {route.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </Collapse>
                          <hr className="my-0 mx-3" style={{ borderColor: 'grey' }} />
                        </li>
                      ) : (
                        <li className="nav-item">
                          <Link
                            to={section.routes[0].path}
                            className={`nav-link menu-link ${isPathActive(section.routes[0]) ? 'active' : ''
                              }`}
                          >
                            <i className={section.icon}></i>
                            <span>
                              {section.section.split(' ').map((word, idx) => (
                                <React.Fragment key={idx}>
                                  {word}
                                  {idx < section.section.split(' ').length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </span>
                          </Link>
                          <hr className="my-0 mx-3" style={{ borderColor: 'grey' }} />
                        </li>
                      )}
                    </React.Fragment>
                  );
                })}
              </ul>
            </Container>
            <div className="position-absolute bottom-0 w-100" style={{ backgroundColor: 'inherit' }}>
              <Container fluid>
                <ul className="navbar-nav">
                  {redirectedFromUMP && (
                    <>
                      <hr className="my-0 mx-3" style={{ borderColor: 'grey' }} />
                      <li className="nav-item">
                        <Link
                          onClick={(e) => {
                            e.preventDefault();
                            handleNavigateToUMP();
                          }}
                          className="nav-link menu-link"
                          to="/#"
                        >
                          <i className="ri-speed-up-line"></i>
                          <span>UMP</span>
                        </Link>
                      </li>
                    </>
                  )}
                  
                  {/* Guide Section */}
                  {window.env.REACT_APP_BASE_URL === "https://data-api.stage.blu-ocean.net" &&
                  (datasource?.includes("datalake") ? (
                    <>
                      <hr className="my-0 mx-3" style={{ borderColor: 'grey' }} />
                      <li className="nav-item sidebar-menu-item">
                        <Link
                          onClick={(e) => {
                            e.preventDefault();
                            toggleSection("Guide");
                          }}
                          className={`nav-link menu-link ${openSections["Guide"] ? 'active' : ''}`}
                          to="/#"
                        >
                          <div className="d-flex">
                            <i className="ri-book-open-line"></i>
                            <span data-key="t-apps" className="align-content-center">Guide</span>
                          </div>
                        </Link>

                        <Collapse
                          className="menu-dropdown"
                          isOpen={openSections["Guide"]}
                          id="sidebar-guide"
                          style={{ visibility: 'visible' }}
                        >
                          <ul className="nav nav-sm flex-column">
                            <li className="nav-item">
                              <Link
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(
                                    `https://datamldocs.sandbox.blu-ocean.net`,
                                    "mlmkdocstab"
                                  );
                                }}
                                className="nav-link ms-4"
                                to="/#"
                              >
                                ML App Guide
                              </Link>
                            </li>
                            <li className="nav-item">
                              <Link
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(
                                    `${window.env?.REACT_APP_BASE_URL || ""}/mkdocs/`,
                                    "mkdocs-tab"
                                  );
                                }}
                                className="nav-link ms-4"
                                to="/#"
                              >
                                Datalake Guide
                              </Link>
                            </li>
                          </ul>
                        </Collapse>
                      </li>
                    </>
                  ) : (
                    <>
                      <hr className="my-0 mx-3" style={{ borderColor: 'grey' }} />
                      <li className="nav-item">
                        <Link
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(
                              `https://datamldocs.sandbox.blu-ocean.net`,
                              "mlmkdocstab"
                            );
                          }}
                          className="nav-link menu-link"
                          to="/#"
                        >
                          <i className="ri-book-open-line"></i>
                          <span>ML App Guide</span>
                        </Link>
                      </li>
                    </>
                  ))}

                  {/* Logout Section */}
                  <hr className="my-0 mx-3" style={{ borderColor: 'grey' }} />
                  <li className="nav-item">
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        setLogoutModalOpen(true);
                      }}
                      className="nav-link menu-link"
                      to="/#"
                    >
                      <i className="ri-logout-box-line"></i>
                      <span>Log Out</span>
                    </Link>
                  </li>
                </ul>
              </Container>
            </div>
          </SimpleBar>
          <div></div>
        </React.Fragment>
      </div>
      <div className="vertical-overlay"></div>
      <LogoutModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleLogout}
      />
    </React.Fragment>
  );
};

export default Sidebar;